<script>
	import Hero from "$components/Hero.svelte";
	import FlowShowcase from "$components/FlowShowcase.svelte";
	import WhatIsOS from "$components/WhatIsOS.svelte";
	import Problem from "$components/Problem.svelte";
	import Solution from "$components/Solution.svelte";
	import Features from "$components/Features.svelte";
	import UseCase from "$components/UseCase.svelte";
	import CTA from "$components/CTA.svelte";
</script>

<svelte:head>
	<title>OS Studio - Build Your Operating System with AI</title>
	<meta
		name="description"
		content="Build and maintain your complete operating system via AI. From requirements to production in hours."
	/>
	<meta
		property="og:title"
		content="OS Studio - Build Your Operating System with AI"
	/>
	<meta
		property="og:description"
		content="Build and maintain your complete operating system via AI. From requirements to production in hours."
	/>
	<meta property="og:image" content="/og-image.png" />
	<meta property="og:type" content="website" />
</svelte:head>

<Hero />
<FlowShowcase />
<WhatIsOS />
<Problem />
<Solution />
<Features />

<!-- Use Cases -->
<section class="py-24 bg-[#0a0a0f] relative">
	<div
		class="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] opacity-30"
	></div>

	<div class="container mx-auto px-6 max-w-6xl relative z-10">
		<div class="text-center mb-16">
			<h2 class="text-4xl md:text-5xl font-bold text-white mb-6">
				Who Benefits From <span class="text-neon-cyan">OS Studio</span>
			</h2>
			<p class="text-xl text-gray-400 max-w-2xl mx-auto">
				Whether you're scaling startups, optimizing enterprises, or
				innovating faster
			</p>
		</div>

		<div class="grid grid-cols-1 md:grid-cols-2 gap-8">
			<UseCase
				title="Startup Founders & CTOs"
				problem="Need complete systems but can't afford big engineering teams"
				solution="Launch your complete operating system in days, not quarters."
				benefits={[
					"10x faster speed to market",
					"No infrastructure expertise needed",
					"Scales from 100 to 100M users",
					"Extensible as you grow",
				]}
			/>
			<UseCase
				title="Enterprise Operations Teams"
				problem="Legacy systems, manual processes, high operational costs"
				solution="Transform your operations into an intelligent, self-managing system."
				benefits={[
					"Reduce operational costs by 60%",
					"Eliminate manual processes",
					"Real-time visibility and control",
					"Compliance built-in",
				]}
			/>
			<UseCase
				title="Digital Transformation Leaders"
				problem="Modernize without disrupting existing operations"
				solution="Your complete operating system, reimagined."
				benefits={[
					"Drop-in replacement for legacy systems",
					"Zero-downtime migrations",
					"Industry-specific templates",
					"Vendor independence",
				]}
			/>
			<UseCase
				title="Non-Technical Innovators"
				problem="Have ideas but blocked by technical limitations"
				solution="Describe your vision. AI builds it. You focus on growth."
				benefits={[
					"No coding required",
					"Instant feedback and iteration",
					"Full control without complexity",
					"Bring ideas to market faster",
				]}
			/>
		</div>
	</div>
</section>

<CTA />
